
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "@/components/ui/textarea";

const formSchema = z.object({
  title: z.string().min(1, { message: "Indtast opgavetitel" }),
  userId: z.string().uuid({ message: "Vælg venligst en medarbejder" }),
  location: z.string().min(1, { message: "Indtast arbejdslokation" }),
  taskType: z.string().min(1, { message: "Vælg opgavetype" }),
  taskDescription: z.string().optional(),
  startTime: z.string().min(1, { message: "Vælg starttidspunkt" }),
  endTime: z.string().min(1, { message: "Vælg sluttidspunkt" }),
  estimatedHours: z.number().min(1, { message: "Angiv estimerede timer" }),
  priority: z.enum(["low", "medium", "high", "critical"], {
    required_error: "Vælg prioritet",
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTaskScheduleProps {
  selectedDate: Date;
}

export const CreateTaskSchedule = ({ selectedDate }: CreateTaskScheduleProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showTaskDescription, setShowTaskDescription] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      userId: "",
      location: "",
      taskType: "electrical_installation",
      taskDescription: "",
      startTime: "09:00",
      endTime: "17:00",
      estimatedHours: 1,
      priority: "medium" as const,
    },
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("first_name", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const createTaskAndScheduleMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // First create the task
      const { data: task, error: taskError } = await supabase
        .from("tasks")
        .insert({
          title: values.title,
          assigned_to: users?.find(user => user.id === values.userId)?.email,
          location: values.location,
          task_type: values.taskType,
          task_description: values.taskDescription,
          priority: values.priority,
          estimated_hours: values.estimatedHours,
          date: format(selectedDate, "yyyy-MM-dd"),
          status: "pending",
          created_by_type: "admin",
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Then create the schedule
      const startDateTime = new Date(selectedDate);
      const [startHours, startMinutes] = values.startTime.split(":");
      startDateTime.setHours(parseInt(startHours), parseInt(startMinutes));

      const endDateTime = new Date(selectedDate);
      const [endHours, endMinutes] = values.endTime.split(":");
      endDateTime.setHours(parseInt(endHours), parseInt(endMinutes));

      const { error: scheduleError } = await supabase
        .from("task_schedules")
        .insert({
          task_id: task.id,
          user_id: values.userId,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
        });

      if (scheduleError) throw scheduleError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-schedules"] });
      toast({
        title: "Opgave oprettet",
        description: "Opgaven er blevet oprettet og planlagt succesfuldt",
      });
      form.reset();
      setShowTaskDescription(false);
    },
    onError: (error) => {
      console.error('Fejl ved oprettelse:', error);
      toast({
        title: "Fejl",
        description: "Der skete en fejl under oprettelse af opgaven",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    createTaskAndScheduleMutation.mutate(values);
  };

  // Watch for changes in taskType to show/hide description field
  const taskType = form.watch("taskType");
  useEffect(() => {
    setShowTaskDescription(taskType === "other_installation");
  }, [taskType]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Opgavetitel</FormLabel>
              <FormControl>
                <Input {...field} className="bg-white border border-gray-200" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Vælg medarbejder</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full bg-white border border-gray-200">
                    <SelectValue placeholder="Vælg en medarbejder" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white shadow-lg z-50">
                  {users?.map((user) => (
                    <SelectItem 
                      key={user.id} 
                      value={user.id}
                      className="hover:bg-gray-100"
                    >
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Arbejdslokation</FormLabel>
              <FormControl>
                <Input {...field} className="bg-white border border-gray-200" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="taskType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Opgavetype</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full bg-white border border-gray-200">
                    <SelectValue placeholder="Vælg opgavetype" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white shadow-lg z-50">
                  <SelectItem value="electrical_installation">EL Installation</SelectItem>
                  <SelectItem value="camera_installation">Kamera Installation</SelectItem>
                  <SelectItem value="alarm_installation">Alarm Installation</SelectItem>
                  <SelectItem value="other_installation">Anden Installation</SelectItem>
                  <SelectItem value="repair">Reparation</SelectItem>
                  <SelectItem value="inspection">Inspektion</SelectItem>
                  <SelectItem value="maintenance">Vedligeholdelse</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {showTaskDescription && (
          <FormField
            control={form.control}
            name="taskDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beskriv installationen</FormLabel>
                <FormControl>
                  <Textarea 
                    {...field} 
                    className="bg-white border border-gray-200"
                    placeholder="Beskriv installationsopgaven..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prioritet</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full bg-white border border-gray-200">
                    <SelectValue placeholder="Vælg prioritet" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white shadow-lg z-50">
                  <SelectItem value="low">Lav</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">Høj</SelectItem>
                  <SelectItem value="critical">Kritisk</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Starttidspunkt</FormLabel>
                <FormControl>
                  <Input type="time" {...field} className="bg-white border border-gray-200" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sluttidspunkt</FormLabel>
                <FormControl>
                  <Input type="time" {...field} className="bg-white border border-gray-200" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="estimatedHours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Estimerede timer</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  {...field} 
                  onChange={e => field.onChange(Number(e.target.value))}
                  min={1}
                  className="bg-white border border-gray-200" 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full bg-primary hover:bg-primary-dark"
          disabled={createTaskAndScheduleMutation.isPending}
        >
          {createTaskAndScheduleMutation.isPending ? "Opretter..." : "Opret opgave"}
        </Button>
      </form>
    </Form>
  );
};

